import React, { useState, useEffect, useCallback } from "react"
import {
  useGetCompositeScoreSetsQuery,
  useLazyGetCompositeScoreBucketsQuery,
  useUpdateCompositeScoreBucketMutation,
  useDeleteCompositeScoreSetMutation,
  useDeleteCompositeScoreTierMutation,
  useDeleteCompositeScoreBucketMutation,
  type CompositeScoreSet,
  type CompositeScoreTier,
  type CompositeScoreBucket,
} from "../../../app/services/meseeknaApiV2"

// Type for tier with measure info
type TierWithMeasure = CompositeScoreTier & {
  measureId?: number
  measureName?: string
  measureCode?: string
}
import DeleteConfirmModal from "./DeleteConfirmModal"
import ScoreSetFormModal from "./ScoreSetFormModal"
import TierFormModal from "./TierFormModal"
import BucketFormModal from "./BucketFormModal"
import OrgMappingModal from "./OrgMappingModal"

// Type for measure grouping
type MeasureGroup = {
  measureId: number
  measureName: string
  measureCode: string
  tiers: CompositeScoreTier[]
}

const ScoreSetsManagement: React.FC = () => {
  // Score set management state
  const [showScoreSetModal, setShowScoreSetModal] = useState(false)
  const [selectedScoreSet, setSelectedScoreSet] = useState<CompositeScoreSet | null>(null)
  const [selectedTier, setSelectedTier] = useState<CompositeScoreTier | null>(null)
  const [tierBuckets, setTierBuckets] = useState<CompositeScoreBucket[]>([])
  const [editedBuckets, setEditedBuckets] = useState<Map<number, { min: number; max: number }>>(
    new Map()
  )

  // Measure grouping state
  const [measureGroups, setMeasureGroups] = useState<MeasureGroup[]>([])
  const [expandedMeasures, setExpandedMeasures] = useState<Set<number>>(new Set())
  const [loadingMeasureGroups, setLoadingMeasureGroups] = useState(false)

  // Main page tier-measure associations (cached for display)
  const [tierMeasureCache, setTierMeasureCache] = useState<Map<number, TierWithMeasure[]>>(new Map())
  const [loadingMeasureCache, setLoadingMeasureCache] = useState(false)

  // CRUD modal states
  const [showScoreSetFormModal, setShowScoreSetFormModal] = useState(false)
  const [showTierFormModal, setShowTierFormModal] = useState(false)
  const [showBucketFormModal, setShowBucketFormModal] = useState(false)
  const [showOrgMappingModal, setShowOrgMappingModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Edit targets
  const [editingScoreSet, setEditingScoreSet] = useState<CompositeScoreSet | null>(null)
  const [editingTier, setEditingTier] = useState<CompositeScoreTier | null>(null)

  // Delete target info
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "set" | "tier" | "bucket"
    id: number
    name: string
  } | null>(null)

  // RTK Query hooks
  const {
    data: scoreSetsData,
    isLoading: scoreSetsLoading,
    refetch: refetchSets,
  } = useGetCompositeScoreSetsQuery()
  const [fetchBuckets] = useLazyGetCompositeScoreBucketsQuery()
  const [updateBucket] = useUpdateCompositeScoreBucketMutation()
  const [deleteSet, { isLoading: deleteSetLoading }] = useDeleteCompositeScoreSetMutation()
  const [deleteTier, { isLoading: deleteTierLoading }] = useDeleteCompositeScoreTierMutation()
  const [deleteBucket, { isLoading: deleteBucketLoading }] = useDeleteCompositeScoreBucketMutation()

  // Helper to parse tiers from the set data
  const parseTiers = useCallback((set: CompositeScoreSet): CompositeScoreTier[] => {
    if (set.tiers && Array.isArray(set.tiers)) {
      return set.tiers
    }
    if (set.tier && typeof set.tier === "string" && set.tier !== "0") {
      try {
        return JSON.parse(set.tier)
      } catch {
        return []
      }
    }
    return []
  }, [])

  // Load measure associations for all score sets (for main page display)
  const loadMeasureAssociations = useCallback(async () => {
    if (!scoreSetsData?.data || scoreSetsData.data.length === 0) return

    setLoadingMeasureCache(true)
    const newCache = new Map<number, TierWithMeasure[]>()

    try {
      // Process each score set
      for (const set of scoreSetsData.data) {
        const tiers = parseTiers(set)
        if (tiers.length === 0) {
          newCache.set(set.id, [])
          continue
        }

        // Fetch buckets for all tiers in parallel
        const bucketPromises = tiers.map((tier) =>
          fetchBuckets(tier.id)
            .unwrap()
            .then((result) => ({ tierId: tier.id, buckets: result.data || [] }))
            .catch(() => ({ tierId: tier.id, buckets: [] }))
        )

        const allResults = await Promise.all(bucketPromises)

        // Build tiers with measure info
        const tiersWithMeasure: TierWithMeasure[] = tiers.map((tier) => {
          const result = allResults.find((r) => r.tierId === tier.id)
          if (result && result.buckets.length > 0) {
            const firstBucket = result.buckets[0]
            return {
              ...tier,
              measureId: firstBucket.measure_id || 0,
              measureName: firstBucket.measureName || `Measure ${firstBucket.measure_id}`,
              measureCode: firstBucket.measureCode || "",
            }
          }
          return { ...tier }
        })

        newCache.set(set.id, tiersWithMeasure)
      }

      setTierMeasureCache(newCache)
    } catch (error) {
      console.error("Error loading measure associations:", error)
    } finally {
      setLoadingMeasureCache(false)
    }
  }, [scoreSetsData?.data, fetchBuckets, parseTiers])

  // Load measure associations when score sets data changes
  useEffect(() => {
    if (scoreSetsData?.data && scoreSetsData.data.length > 0) {
      loadMeasureAssociations()
    }
  }, [scoreSetsData?.data, loadMeasureAssociations])

  // Helper to get unique measures from tiers
  const getUniqueMeasures = (tiers: TierWithMeasure[]): { measureId: number; measureName: string; measureCode: string; count: number }[] => {
    const measureMap = new Map<number, { measureId: number; measureName: string; measureCode: string; count: number }>()
    for (const tier of tiers) {
      if (tier.measureId) {
        if (!measureMap.has(tier.measureId)) {
          measureMap.set(tier.measureId, {
            measureId: tier.measureId,
            measureName: tier.measureName || "",
            measureCode: tier.measureCode || "",
            count: 0,
          })
        }
        measureMap.get(tier.measureId)!.count++
      }
    }
    return Array.from(measureMap.values()).sort((a, b) => a.measureName.localeCompare(b.measureName))
  }

  // Build measure groups by fetching buckets for all tiers
  const buildMeasureGroups = async (tiers: CompositeScoreTier[]): Promise<MeasureGroup[]> => {
    if (tiers.length === 0) return []

    // Fetch buckets for all tiers in parallel
    const bucketPromises = tiers.map((tier) =>
      fetchBuckets(tier.id)
        .unwrap()
        .then((result) => ({ tierId: tier.id, buckets: result.data || [] }))
        .catch(() => ({ tierId: tier.id, buckets: [] }))
    )

    const allResults = await Promise.all(bucketPromises)

    // Build a map of tier -> primary measure (using first bucket's measure)
    const tierMeasureMap = new Map<number, { measureId: number; measureName: string; measureCode: string }>()
    for (const result of allResults) {
      if (result.buckets.length > 0) {
        const firstBucket = result.buckets[0]
        tierMeasureMap.set(result.tierId, {
          measureId: firstBucket.measure_id || 0,
          measureName: firstBucket.measureName || `Measure ${firstBucket.measure_id}`,
          measureCode: firstBucket.measureCode || "",
        })
      }
    }

    // Group tiers by measure
    const measureMap = new Map<number, MeasureGroup>()
    for (const tier of tiers) {
      const measureInfo = tierMeasureMap.get(tier.id)
      if (measureInfo) {
        if (!measureMap.has(measureInfo.measureId)) {
          measureMap.set(measureInfo.measureId, {
            measureId: measureInfo.measureId,
            measureName: measureInfo.measureName,
            measureCode: measureInfo.measureCode,
            tiers: [],
          })
        }
        measureMap.get(measureInfo.measureId)!.tiers.push(tier)
      } else {
        // Tiers without buckets go into an "Uncategorized" group
        if (!measureMap.has(0)) {
          measureMap.set(0, {
            measureId: 0,
            measureName: "Uncategorized",
            measureCode: "",
            tiers: [],
          })
        }
        measureMap.get(0)!.tiers.push(tier)
      }
    }

    // Sort groups by measure name and return
    return Array.from(measureMap.values()).sort((a, b) =>
      a.measureName.localeCompare(b.measureName)
    )
  }

  // Toggle measure accordion
  const toggleMeasure = (measureId: number) => {
    setExpandedMeasures((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(measureId)) {
        newSet.delete(measureId)
      } else {
        newSet.add(measureId)
      }
      return newSet
    })
  }

  // Score Set handlers
  const openScoreSetModal = async (set: CompositeScoreSet) => {
    // Parse tiers from the tier JSON string if needed
    const parsedTiers = parseTiers(set)
    const setWithParsedTiers = { ...set, tiers: parsedTiers }
    setSelectedScoreSet(setWithParsedTiers)
    setSelectedTier(null)
    setTierBuckets([])
    setEditedBuckets(new Map())
    setMeasureGroups([])
    setExpandedMeasures(new Set())
    setShowScoreSetModal(true)

    // Build measure groups for better organization (only if many tiers)
    if (parsedTiers.length > 10) {
      setLoadingMeasureGroups(true)
      try {
        const groups = await buildMeasureGroups(parsedTiers)
        setMeasureGroups(groups)
        // Expand first measure by default
        if (groups.length > 0) {
          setExpandedMeasures(new Set([groups[0].measureId]))
        }
      } catch (error) {
        console.error("Error building measure groups:", error)
      } finally {
        setLoadingMeasureGroups(false)
      }
    }
  }

  const handleSelectTier = async (tier: CompositeScoreTier) => {
    setSelectedTier(tier)
    setEditedBuckets(new Map())
    try {
      const result = await fetchBuckets(tier.id).unwrap()
      setTierBuckets(result.data || [])
    } catch (error) {
      console.error("Error fetching buckets:", error)
      setTierBuckets([])
    }
  }

  const handleBucketChange = (bucketId: number, field: "min" | "max", value: string) => {
    const numValue = parseInt(value, 10) || 0
    setEditedBuckets((prev) => {
      const newMap = new Map(prev)
      const existing = newMap.get(bucketId) || { min: 0, max: 0 }
      newMap.set(bucketId, { ...existing, [field]: numValue })
      return newMap
    })
  }

  const handleSaveBuckets = async () => {
    if (editedBuckets.size === 0) return

    try {
      const bucketsToUpdate = Array.from(editedBuckets.entries())
      for (let i = 0; i < bucketsToUpdate.length; i++) {
        const [bucketId, values] = bucketsToUpdate[i]
        await updateBucket({
          id: bucketId,
          min_score: values.min,
          max_score: values.max,
        }).unwrap()
      }

      // Refresh buckets
      if (selectedTier) {
        const result = await fetchBuckets(selectedTier.id).unwrap()
        setTierBuckets(result.data || [])
      }
      setEditedBuckets(new Map())
      alert("Buckets saved successfully!")
    } catch (error) {
      console.error("Error saving buckets:", error)
      alert("Error saving buckets. Please try again.")
    }
  }

  const closeModal = () => {
    setShowScoreSetModal(false)
    setSelectedScoreSet(null)
    setSelectedTier(null)
    setTierBuckets([])
    setEditedBuckets(new Map())
    setMeasureGroups([])
    setExpandedMeasures(new Set())
  }

  // CRUD handlers
  const handleCreateScoreSet = () => {
    setEditingScoreSet(null)
    setShowScoreSetFormModal(true)
  }

  const handleEditScoreSet = (set: CompositeScoreSet) => {
    setEditingScoreSet(set)
    setShowScoreSetFormModal(true)
  }

  const handleDeleteScoreSet = (set: CompositeScoreSet) => {
    setDeleteTarget({ type: "set", id: set.id, name: set.name || `Set #${set.id}` })
    setShowDeleteConfirm(true)
  }

  const handleManageOrgs = (set: CompositeScoreSet) => {
    const parsedTiers = parseTiers(set)
    setSelectedScoreSet({ ...set, tiers: parsedTiers })
    setShowOrgMappingModal(true)
  }

  const handleCreateTier = () => {
    setEditingTier(null)
    setShowTierFormModal(true)
  }

  const handleEditTier = (tier: CompositeScoreTier) => {
    setEditingTier(tier)
    setShowTierFormModal(true)
  }

  const handleDeleteTier = (tier: CompositeScoreTier) => {
    setDeleteTarget({ type: "tier", id: tier.id, name: tier.name || `Tier #${tier.id}` })
    setShowDeleteConfirm(true)
  }

  const handleAddBucket = () => {
    setShowBucketFormModal(true)
  }

  const handleDeleteBucket = (bucket: CompositeScoreBucket) => {
    setDeleteTarget({
      type: "bucket",
      id: bucket.id,
      name: bucket.measureName || `Bucket #${bucket.id}`,
    })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === "set") {
        await deleteSet(deleteTarget.id).unwrap()
        refetchSets()
        closeModal()
      } else if (deleteTarget.type === "tier") {
        await deleteTier(deleteTarget.id).unwrap()
        refetchSets()
        // Update the selected score set's tiers
        if (selectedScoreSet) {
          const updatedTiers = (selectedScoreSet.tiers || []).filter(
            (t) => t.id !== deleteTarget.id
          )
          setSelectedScoreSet({ ...selectedScoreSet, tiers: updatedTiers })
        }
        if (selectedTier?.id === deleteTarget.id) {
          setSelectedTier(null)
          setTierBuckets([])
        }
      } else if (deleteTarget.type === "bucket") {
        await deleteBucket(deleteTarget.id).unwrap()
        if (selectedTier) {
          const result = await fetchBuckets(selectedTier.id).unwrap()
          setTierBuckets(result.data || [])
        }
      }
      setShowDeleteConfirm(false)
      setDeleteTarget(null)
    } catch (error) {
      console.error("Delete failed:", error)
      alert("Failed to delete. Please try again.")
    }
  }

  // Modal save callbacks
  const handleScoreSetSaved = () => {
    setShowScoreSetFormModal(false)
    setEditingScoreSet(null)
    refetchSets()
  }

  const handleTierSaved = async () => {
    setShowTierFormModal(false)
    setEditingTier(null)
    await refetchSets()
    // Refresh the selected score set's tiers
    if (selectedScoreSet) {
      const updatedSets = scoreSetsData?.data || []
      const updatedSet = updatedSets.find((s) => s.id === selectedScoreSet.id)
      if (updatedSet) {
        const parsedTiers = parseTiers(updatedSet)
        setSelectedScoreSet({ ...updatedSet, tiers: parsedTiers })
      }
    }
  }

  const handleBucketSaved = async () => {
    setShowBucketFormModal(false)
    if (selectedTier) {
      const result = await fetchBuckets(selectedTier.id).unwrap()
      setTierBuckets(result.data || [])
    }
  }

  const handleOrgMappingSaved = () => {
    setShowOrgMappingModal(false)
  }

  return (
    <div className="py-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Composite Score Sets</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage composite score sets, tiers, and bucket score ranges.
          </p>
        </div>
        <button
          onClick={handleCreateScoreSet}
          className="bg-indigo-600 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer transition-colors"
        >
          Create Score Set
        </button>
      </div>

      {scoreSetsLoading ? (
        <div className="bg-white shadow sm:rounded-md p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading score sets...</div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Set Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Measures & Tiers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scoreSetsData?.data?.map((set) => {
                const tiersWithMeasure = tierMeasureCache.get(set.id) || []
                const uniqueMeasures = getUniqueMeasures(tiersWithMeasure)
                const allTiers = parseTiers(set)

                return (
                <tr key={set.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {set.name || `Set #${set.id}`}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {loadingMeasureCache ? (
                      <span className="text-sm text-gray-400">Loading...</span>
                    ) : uniqueMeasures.length > 0 ? (
                      <div className="space-y-2">
                        {uniqueMeasures.map((measure) => {
                          // Get tiers for this measure
                          const measureTiers = tiersWithMeasure.filter(
                            (t) => t.measureId === measure.measureId
                          )
                          return (
                            <div key={measure.measureId} className="flex flex-wrap items-center gap-1">
                              <span className="text-xs font-medium text-gray-600 mr-1">
                                {measure.measureCode || measure.measureName}:
                              </span>
                              {measureTiers.map((tier) => (
                                <span
                                  key={tier.id}
                                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border"
                                  style={{
                                    backgroundColor: tier.color ? `${tier.color}20` : "#f3f4f6",
                                    borderColor: tier.color || "#d1d5db",
                                    color: "#1f2937",
                                  }}
                                >
                                  <span
                                    className="w-2 h-2 rounded-full mr-1.5"
                                    style={{ backgroundColor: tier.color || "#6b7280" }}
                                  />
                                  {tier.name || `Tier ${tier.id}`}
                                </span>
                              ))}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      // Fallback: show tiers without measure info
                      <div className="flex flex-wrap gap-1">
                        {allTiers.map((tier) => (
                          <span
                            key={tier.id}
                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border"
                            style={{
                              backgroundColor: tier.color ? `${tier.color}20` : "#f3f4f6",
                              borderColor: tier.color || "#d1d5db",
                              color: "#1f2937",
                            }}
                          >
                            <span
                              className="w-2 h-2 rounded-full mr-1.5"
                              style={{ backgroundColor: tier.color || "#6b7280" }}
                            />
                            {tier.name || `Tier ${tier.id}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {set.createdOn ? new Date(set.createdOn).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => openScoreSetModal(set)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium cursor-pointer transition-colors"
                      >
                        Edit Buckets
                      </button>
                      <button
                        onClick={() => handleEditScoreSet(set)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium cursor-pointer transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleManageOrgs(set)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium cursor-pointer transition-colors"
                      >
                        Orgs
                      </button>
                      <button
                        onClick={() => handleDeleteScoreSet(set)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium cursor-pointer transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                )
              })}
              {(!scoreSetsData?.data || scoreSetsData.data.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    No score sets found. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Score Set Editor Modal */}
      {showScoreSetModal && selectedScoreSet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium">
                Edit Score Set: {selectedScoreSet.name || `Set #${selectedScoreSet.id}`}
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCreateTier}
                  className="bg-green-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-green-700 cursor-pointer transition-colors"
                >
                  Add Tier
                </button>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500 cursor-pointer"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            {/* Tier Selection */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Select Tier</h4>

              {/* Loading state for measure groups */}
              {loadingMeasureGroups && (
                <div className="text-sm text-gray-500 mb-3">
                  Organizing tiers by measure...
                </div>
              )}

              {/* Grouped view (for score sets with many tiers) */}
              {measureGroups.length > 0 && !loadingMeasureGroups ? (
                <div className="space-y-2">
                  {measureGroups.map((group) => (
                    <div key={group.measureId} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Measure header (accordion toggle) */}
                      <button
                        onClick={() => toggleMeasure(group.measureId)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-900">
                            {group.measureName}
                          </span>
                          {group.measureCode && (
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                              {group.measureCode}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            ({group.tiers.length} tier{group.tiers.length !== 1 ? "s" : ""})
                          </span>
                        </div>
                        <span className="text-gray-400 text-lg">
                          {expandedMeasures.has(group.measureId) ? "−" : "+"}
                        </span>
                      </button>

                      {/* Tiers for this measure */}
                      {expandedMeasures.has(group.measureId) && (
                        <div className="px-4 py-3 bg-white border-t border-gray-100">
                          <div className="flex flex-wrap gap-2">
                            {group.tiers.map((tier) => (
                              <div key={tier.id} className="flex items-center">
                                <button
                                  onClick={() => handleSelectTier(tier)}
                                  className={`px-4 py-2 rounded-l-md text-sm font-medium transition-colors cursor-pointer ${
                                    selectedTier?.id === tier.id
                                      ? "bg-indigo-600 text-white"
                                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                                  style={
                                    selectedTier?.id === tier.id
                                      ? {}
                                      : { borderLeft: `4px solid ${tier.color || "#9ca3af"}` }
                                  }
                                >
                                  {tier.name || `Tier ${tier.id}`}
                                  {tier.output_score !== null && (
                                    <span className="ml-2 text-xs opacity-75">
                                      (Score: {tier.output_score})
                                    </span>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleEditTier(tier)}
                                  className="px-2 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
                                  title="Edit tier"
                                >
                                  &#9998;
                                </button>
                                <button
                                  onClick={() => handleDeleteTier(tier)}
                                  className="px-2 py-2 bg-gray-100 text-red-600 hover:bg-red-100 rounded-r-md cursor-pointer transition-colors"
                                  title="Delete tier"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : !loadingMeasureGroups ? (
                /* Flat view (for score sets with fewer tiers) */
                <div className="flex flex-wrap gap-2">
                  {selectedScoreSet.tiers?.map((tier) => (
                    <div key={tier.id} className="flex items-center">
                      <button
                        onClick={() => handleSelectTier(tier)}
                        className={`px-4 py-2 rounded-l-md text-sm font-medium transition-colors cursor-pointer ${
                          selectedTier?.id === tier.id
                            ? "bg-indigo-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        style={
                          selectedTier?.id === tier.id
                            ? {}
                            : { borderLeft: `4px solid ${tier.color || "#9ca3af"}` }
                        }
                      >
                        {tier.name || `Tier ${tier.id}`}
                        {tier.output_score !== null && (
                          <span className="ml-2 text-xs opacity-75">
                            (Score: {tier.output_score})
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleEditTier(tier)}
                        className="px-2 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
                        title="Edit tier"
                      >
                        &#9998;
                      </button>
                      <button
                        onClick={() => handleDeleteTier(tier)}
                        className="px-2 py-2 bg-gray-100 text-red-600 hover:bg-red-100 rounded-r-md cursor-pointer transition-colors"
                        title="Delete tier"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  {(!selectedScoreSet.tiers || selectedScoreSet.tiers.length === 0) && (
                    <p className="text-sm text-gray-500">
                      No tiers yet. Click &quot;Add Tier&quot; to create one.
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            {/* Buckets Table */}
            {selectedTier && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-medium text-gray-700">
                    Buckets for {selectedTier.name || `Tier ${selectedTier.id}`}
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddBucket}
                      className="bg-green-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-green-700 cursor-pointer transition-colors"
                    >
                      Add Bucket
                    </button>
                    <button
                      onClick={handleSaveBuckets}
                      disabled={editedBuckets.size === 0}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Measure
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Min Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Max Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tierBuckets.map((bucket) => {
                        const edited = editedBuckets.get(bucket.id)
                        return (
                          <tr key={bucket.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {bucket.measureName || `Measure ${bucket.measure_id}`}
                              </div>
                              <div className="text-xs text-gray-500">{bucket.measureCode}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={edited?.min ?? bucket.min_score ?? ""}
                                onChange={(e) =>
                                  handleBucketChange(bucket.id, "min", e.target.value)
                                }
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="0-100"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={edited?.max ?? bucket.max_score ?? ""}
                                onChange={(e) =>
                                  handleBucketChange(bucket.id, "max", e.target.value)
                                }
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="0-100"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                onClick={() => handleDeleteBucket(bucket)}
                                className="text-red-600 hover:text-red-900 cursor-pointer text-lg"
                                title="Delete bucket"
                              >
                                &times;
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                      {tierBuckets.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                            No buckets found for this tier. Click &quot;Add Bucket&quot; to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!selectedTier && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  Select a tier above to view and edit its bucket score ranges.
                </p>
              </div>
            )}

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score Set Form Modal */}
      <ScoreSetFormModal
        scoreSet={editingScoreSet}
        isOpen={showScoreSetFormModal}
        onClose={() => {
          setShowScoreSetFormModal(false)
          setEditingScoreSet(null)
        }}
        onSave={handleScoreSetSaved}
      />

      {/* Tier Form Modal */}
      {selectedScoreSet && (
        <TierFormModal
          tier={editingTier}
          scoreSetId={selectedScoreSet.id}
          isOpen={showTierFormModal}
          onClose={() => {
            setShowTierFormModal(false)
            setEditingTier(null)
          }}
          onSave={handleTierSaved}
        />
      )}

      {/* Bucket Form Modal */}
      {selectedTier && (
        <BucketFormModal
          tierId={selectedTier.id}
          isOpen={showBucketFormModal}
          onClose={() => setShowBucketFormModal(false)}
          onSave={handleBucketSaved}
        />
      )}

      {/* Org Mapping Modal */}
      {selectedScoreSet && (
        <OrgMappingModal
          scoreSetId={selectedScoreSet.id}
          scoreSetName={selectedScoreSet.name || `Set #${selectedScoreSet.id}`}
          isOpen={showOrgMappingModal}
          onClose={() => setShowOrgMappingModal(false)}
          onSave={handleOrgMappingSaved}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        title={`Delete ${
          deleteTarget?.type === "set"
            ? "Score Set"
            : deleteTarget?.type === "tier"
              ? "Tier"
              : "Bucket"
        }`}
        message={`Are you sure you want to delete this ${deleteTarget?.type}? This action cannot be undone.`}
        itemName={deleteTarget?.name || ""}
        isLoading={deleteSetLoading || deleteTierLoading || deleteBucketLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setDeleteTarget(null)
        }}
      />
    </div>
  )
}

export default ScoreSetsManagement
